import {
    Component,
    ElementRef,
    Output,
    ViewChild,
    EventEmitter,
    Renderer2,
    OnDestroy,
    OnInit,
    Input,
    ChangeDetectionStrategy,
    AfterViewInit,
    HostListener
} from '@angular/core';
import { Subject, BehaviorSubject, ReplaySubject, tap } from 'rxjs';
import { UiQuery } from '../../state/ui/ui.query';
import { CommonModule } from '@angular/common';
import { fadeInOutModal } from '../../animations/fade-in-out.animation';
import { HammerModule } from '@angular/platform-browser';
import { ModalTemplateOptions, SbGenericModalService } from './sb-generic-modal.service';
import { AnimationEvent } from '@angular/animations';

const TOUCH_MOVE_BREAKPOINT = 50; // Reasonably if touch move down more than 50px, this should be the "close" trigger

@Component({
    selector: 'sb-generic-modal',
    templateUrl: './sb-generic-modal.component.html',
    styleUrls: ['./sb-generic-modal.component.scss'],
    imports: [CommonModule, HammerModule],
    animations: [fadeInOutModal],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbGenericModalComponent implements OnDestroy, OnInit, AfterViewInit {
    maxNegativeDelta = 0;

    @ViewChild('modalHeader') modalHeader: ElementRef;
    @ViewChild('modalWrapper') modalWrapper: ElementRef;
    @ViewChild('modalContainer') modalContainer: ElementRef;
    @ViewChild('backdropElement') backdropElement: ElementRef;
    @Output() modalDismiss = new EventEmitter<boolean>();

    _options?: ModalTemplateOptions;
    private _options$ = new ReplaySubject<ModalTemplateOptions>(1);
    options$ = this._options$.pipe(tap((options) => (this._options = options)));
    @Input() set options(value: ModalTemplateOptions) {
        if (value) {
            if (!value.position) {
                value.position = 'aside-right';
            }
            if (!value.centerModal) {
                value.centerModal = false;
            }
            this._options$.next(value);
        }
    }

    private _modalFadeTransform$ = new BehaviorSubject<string>(`translate3d(0,0,0)`);
    private _transitionApplied = false;

    // This is used to update the translate state for the :leave animation
    // This makes the animation start from the current Y position of the translate, instead of 0
    get modalFadeTransform$() {
        return this._modalFadeTransform$.asObservable();
    }
    set modalFadeTransform(newTransform: string) {
        this._modalFadeTransform$.next(newTransform);
    }

    get transitionApplied() {
        return this._transitionApplied;
    }
    set transitionApplied(alreadyApplied: boolean) {
        this._transitionApplied = alreadyApplied;
    }

    firstClickRegistered = false;

    viewportSize = this._uiQuery.viewportSize;
    modalObserver$: ResizeObserver;

    unsubscribe$ = new Subject();

    /**
     * Listens for clicks on the viewport to allow clicking the background to close a standalone modal
     * @param ev
     * @returns
     */
    @HostListener('document:click', ['$event'])
    onScreenClick(ev: MouseEvent) {
        // Ignore first click (this is the click that opens the modal)
        if (!this.firstClickRegistered) {
            this.firstClickRegistered = true;
            return;
        }
        if (!this._options.isStandalone) {
            // If modal is not standalone, there needs to be a backdrop element...
            // ... on the same DOM level in the component where this modal is declared
            // ... e.g., the backdrop close click needs to be handled manually there
            // TODO: This really should be handled here, but for z-index reasons, it's not possible right now
            return;
        }

        const contentCoords: DOMRect =
            this.modalWrapper.nativeElement.getBoundingClientRect();
        const { top, right, left, bottom } = contentCoords;
        if (
            ev.clientX < right &&
            ev.clientX > left &&
            ev.clientY < bottom &&
            ev.clientY > top
        ) {
            return;
        }

        this.closeModal();
    }

    constructor(
        private _uiQuery: UiQuery,
        private _renderer: Renderer2,
        private _genericModalService: SbGenericModalService
    ) {}

    ngOnInit(): void {
        // If any modal is still opened, make sure we close it to not get overlapping modals
        this._genericModalService.dismissOpenedModal();
    }
    ngAfterViewInit(): void {
        this._genericModalService.setModalOpened(this);

        this.maxNegativeDelta =
            (4 / 7) * Math.floor(this.modalWrapper.nativeElement.clientHeight);

        // Modal height may change due to late rendering of content or expansion due to other factors
        this.modalObserver$ = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === this.modalWrapper.nativeElement) {
                    const { height } = entry.contentRect;
                    this.maxNegativeDelta = (4 / 7) * Math.floor(height);
                }
            }
        });
        this.modalObserver$.observe(this.modalWrapper.nativeElement);
    }

    onPanUp(ev) {
        if (!this._options.allowDragResize) {
            return;
        }
        if (this.viewportSize === 'xs') {
            this._applyDragAnimation(ev.deltaY);
        }
    }

    onPanDown(ev) {
        // Only allow dragging to take place in xs mode
        if (!this._options.allowDragResize) {
            return;
        }
        if (this.viewportSize === 'xs') {
            this._applyDragAnimation(ev.deltaY);
        }
    }

    onSwipeDown(ev) {
        this.closeModal();
    }

    onPanEnd(ev) {
        // Dragging is only allowed to take place in xs mode
        if (!(this.viewportSize === 'xs' && this._options.allowDragResize)) {
            return;
        }
        if (ev.deltaY > TOUCH_MOVE_BREAKPOINT) {
            setTimeout(() => this.closeModal(), 1);
            return;
        }
        this._revertDragAnimation();
    }

    onHeaderTap(ev) {
        // Only allow tap close to take place in xs mode
        if (this.viewportSize === 'xs' && this._options.allowTapClose) {
            return ev.tapCount > 1 && this.closeModal();
        }
    }

    private _applyDragAnimation(newDelta: number) {
        let delta = newDelta;
        let scaleY = 1;
        if (newDelta < 0) {
            const maxDelta = 10;
            scaleY = Math.min(1 + Math.abs(newDelta / (maxDelta * 10)), 1.05);
            delta = -Math.min(maxDelta, Math.abs(newDelta));
        } else {
            delta = Math.min(Math.abs(newDelta), this.maxNegativeDelta);

            // Lower blur and backdrop transparency on drag down relative to drag distance
            if (this.backdropElement?.nativeElement) {
                const maxBlur = 3; //px
                const maxAlpha = 0.2;
                const relBlur =
                    maxBlur - (Math.abs(delta) / this.maxNegativeDelta) * maxBlur; // s.t., newDelta == maxDelta -> blur = 2 - 2
                const relAlpha =
                    maxAlpha - (Math.abs(delta) / this.maxNegativeDelta) * maxAlpha; // s.t., newDelta == maxDelta -> alpha = 0.15 - 0.15
                this._renderer.setStyle(
                    this.backdropElement.nativeElement,
                    'backdrop-filter',
                    `blur(${relBlur}px)`
                );
                this._renderer.setStyle(
                    this.backdropElement.nativeElement,
                    'background',
                    `rgba(0,0,0,${relAlpha})`
                );
            }
        }

        // We only need to set the new transition rule once per drag initiation
        if (!this.transitionApplied) {
            this._renderer.setStyle(
                this.modalWrapper.nativeElement,
                'transition',
                `37ms linear`
            );
            this.transitionApplied = true;
        }

        this.modalFadeTransform = `translate3d(0,${delta}px,0)`;
        this._renderer.setStyle(
            this.modalWrapper.nativeElement,
            'transform',
            `translate3d(0,${delta}px,0) scale3d(1,${scaleY},1)`
        );
    }

    private _revertDragAnimation() {
        // Revert styling of wrapper
        this.modalFadeTransform = `translate3d(0,0,0)`;
        this._renderer.removeStyle(this.modalWrapper.nativeElement, 'transition');
        this._renderer.setStyle(
            this.modalWrapper.nativeElement,
            'transform',
            `translate3d(0,0,0) scaleY(1)`
        );
        if (this.backdropElement?.nativeElement) {
            this._renderer.removeStyle(
                this.backdropElement.nativeElement,
                'backdrop-filter'
            );
            this._renderer.removeStyle(this.backdropElement.nativeElement, 'background');
        }
        this.transitionApplied = false;
    }

    onFadeAnimationEnd(event: AnimationEvent) {
        if (event.toState === 'void') {
            this._genericModalService.afterLeaveAnimationSubject$.next(true);
        }
    }

    closeModal() {
        this.modalDismiss.emit(true);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
        this.modalObserver$.disconnect();
    }
}
